import React, { useEffect } from 'react';
import {
  StyledFormSelect,
  StyledSearchButton,
  StyledSearchInput,
  StyledSearchWrapper,
} from 'pages/DashboardPage/GlobalSearch/styles';
import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import { InputAdornment } from '@mui/material';
import Icon from 'components/atoms/Icon/Icon';
import { EIconTypes } from 'constants/Icons';
import { EButtonVariants } from 'constants/Buttons';
import { useNavigate } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { EGlobalSearchCategories } from 'models/dashboard';
import { usePermission } from 'hooks/usePermission';
import { EPermission } from 'models/permissions';
import { useTranslations } from 'hooks/useTranslations';
import { Box } from '@mui/system';
import { useWeaponLicenceModule } from 'hooks/useWeaponLicenceModule';
import { colors } from 'theme/colors';

interface IGlobalSearchForm {
  category: string[] | undefined;
  query: string;
}

interface IGlobalSearch {
  query?: string | null;
  category?: string[] | null;
  isLoadingData?: boolean;
}

const initialFormValues: IGlobalSearchForm = {
  category: [],
  query: '',
};

const GlobalSearch = ({ query = '', category = [], isLoadingData = false }: IGlobalSearch) => {
  const navigate = useNavigate();
  const { hasWeaponLicenceModule } = useWeaponLicenceModule();
  const { hasPermissions } = usePermission();
  const { t } = useTranslations();

  const searchCategories = [
    ...(hasPermissions([EPermission.VIEW_ALL_LE, EPermission.VIEW_LE], true)
      ? [
        {
          label: t('dashboard.search.legalEntitiesIndividual.label'),
          value: EGlobalSearchCategories.LegalEntitiesIndividual,
        },
        {
          label: t('dashboard.search.legalEntitiesOrganisation.label'),
          value: EGlobalSearchCategories.LegalEntitiesOrganisation,
        },
      ]
      : []),

    ...(hasPermissions([EPermission.VIEW_ALL_FIREARMS, EPermission.VIEW_FIREARMS], true)
      ? [
        {
          label: t('dashboard.search.firearms.label'),
          value: EGlobalSearchCategories.Firearms,
        },
      ]
      : []),

    ...(hasPermissions(
      [EPermission.VIEW_LIMITED_ANCILLARIES, EPermission.VIEW_ALL_ANCILLARIES],
      true,
    )
      ? [
        {
          label: t('dashboard.search.accessories.label'),
          value: EGlobalSearchCategories.Accessories,
        },
      ]
      : []),

    ...(hasPermissions(
      [EPermission.VIEW_LIMITED_ANCILLARIES, EPermission.VIEW_ALL_ANCILLARIES],
      true,
    )
      ? [
        {
          label: t('dashboard.search.ammunition.label'),
          value: EGlobalSearchCategories.Ammunition,
        },
      ]
      : []),

    ...(hasPermissions(
      [EPermission.VIEW_LIMITED_ANCILLARIES, EPermission.VIEW_ALL_ANCILLARIES],
      true,
    )
      ? [
        {
          label: t('dashboard.search.components.label'),
          value: EGlobalSearchCategories.Components,
        },
      ]
      : []),

    ...(hasPermissions(
      [EPermission.VIEW_TRANSACTIONS, EPermission.VIEW_ALL_TRANSACTIONS],
      true,
    )
      ? [
        {
          label: t('dashboard.search.transactions.label'),
          value: EGlobalSearchCategories.Transactions,
        },
      ]
      : []),

    ...(hasPermissions([EPermission.MANAGE_USERS])
      ? [
        {
          label: t('dashboard.search.users.label'),
          value: EGlobalSearchCategories.Users,
        },
      ]
      : []),

    ...(hasPermissions([EPermission.VIEW_GLOBAL_LICENCES]) &&
      hasWeaponLicenceModule() !== false
      ? [
        {
          label: t('dashboard.search.licences.label'),
          value: EGlobalSearchCategories.Licences,
        },
      ]
      : []),
  ];

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm<IGlobalSearchForm>({
    defaultValues: initialFormValues,
  });

  useEffect(() => {
    reset({
      ...initialFormValues,
      ...(query ? { query } : {}),
      ...(category && category?.length ? { category: category } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (values: IGlobalSearchForm) => {
    const queryValue = values?.query.trim();
    const category = values?.category ? values?.category.join(',') : null;
    navigate(
      `${ERouteLinks.DashboardSearchResultsPage}?query=${encodeURIComponent(queryValue)}${category ? `&category=${category}` : ''
      }`,
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <StyledSearchWrapper container>
        <Grid
          container
          size={{ xs: 12 }}
          rowSpacing={1}
          sx={{ height: 'fit-content', alignSelf: 'center' }}
        >
          <Grid size={{ xs: 12, lg: 3 }}>
            <StyledFormSelect
              options={searchCategories}
              name="category"
              setValue={setValue}
              control={control}
              errors={errors}
              withValidation={false}
              withLabel={false}
              disabled={isLoadingData}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 7 }}>
            <StyledSearchInput
              name="query"
              control={control}
              errors={errors}
              setValue={setValue}
              placeholder={`${t('general.search.label')}...`}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon
                      type={EIconTypes.search}
                      color={isLoadingData ? colors.gray400 : undefined}
                    />
                  </InputAdornment>
                ),
              }}
              withValidation={false}
              withLabel={false}
              disabled={isLoadingData}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <StyledSearchButton
                type="submit"
                variant={EButtonVariants.contained}
                label={t('general.search.label')}
                fullWidth
                disabled={isLoadingData}
              />
            </Box>
          </Grid>
        </Grid>
      </StyledSearchWrapper>
    </form>
  );
};

export default GlobalSearch;
